import React, { useState, useEffect, useMemo, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { NodeProps, NodeResizer, useStore } from 'reactflow';
import { FiPackage, FiX, FiEdit2 } from 'react-icons/fi';
import './GroupNode.css';

interface GroupData {
  label: string;
  color: string;
  onUpdate: (label: string, color: string) => void;
  onDelete: () => void;
  [key: string]: any;
}

const FABRIC_SKU_COSTS: Record<string, number> = {
  Trial: 0,
  F2: 262, F4: 524, F8: 1048, F16: 2096,
  F32: 4192, F64: 8384, F128: 16768,
};

const FABRIC_ICONS: Record<string, string> = {
  fabric_capacity: '/icons/fabric/fabric_capacity.svg',
  fabric_workspace: '/icons/fabric/group_workspace_64_non-item.svg',
  fabric_folder: '/icons/fabric/folder_64_non-item.svg',
};

const GroupNode: React.FC<NodeProps<GroupData>> = memo(({ id, data, selected }) => {
  const { t } = useTranslation('canvas');
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(data.label);
  const [color, setColor] = useState(data.color || '#DBEAFE');

  const nodeInternals = useStore((s) => s.nodeInternals);

  const fabricFinOps = useMemo(() => {
    const rType: string = data.type || data.resourceType || '';

    if (rType === 'fabric_capacity') {
      const sku: string = data.params?.sku || 'F2';
      const cost = FABRIC_SKU_COSTS[sku] ?? 0;
      return { kind: 'capacity' as const, sku, cost };
    }

    if (rType === 'fabric_workspace') {
      const thisNode = nodeInternals.get(id);
      if (!thisNode?.parentNode) return null;
      const parentCap = nodeInternals.get(thisNode.parentNode);
      if (!parentCap || (parentCap.data as any)?.type !== 'fabric_capacity') return null;
      const sku: string = (parentCap.data as any)?.params?.sku || 'F2';
      const totalCost = FABRIC_SKU_COSTS[sku] ?? 0;
      const siblings = Array.from(nodeInternals.values()).filter(
        (n) => n.parentNode === thisNode.parentNode && (n.data as any)?.type === 'fabric_workspace'
      ).length;
      const share = siblings > 0 ? Math.round(totalCost / siblings) : totalCost;
      return { kind: 'workspace' as const, sku, share, siblings };
    }

    return null;
  }, [id, data, nodeInternals]);

  // Synchronize color when data.color changes
  useEffect(() => {
    if (data.color && data.color !== color) {
      setColor(data.color);
    }
  }, [data.color, color]);

  const handleSave = () => {
    data.onUpdate?.(label, color);
    setEditing(false);
  };

  const colors = useMemo(() => [
    { name: t('groupNode.colors.blue'), value: '#DBEAFE' },
    { name: t('groupNode.colors.green'), value: '#D1FAE5' },
    { name: t('groupNode.colors.pink'), value: '#FCE7F3' },
    { name: t('groupNode.colors.orange'), value: '#FED7AA' },
    { name: t('groupNode.colors.purple'), value: '#E9D5FF' },
    { name: t('groupNode.colors.gray'), value: '#F3F4F6' },
  ], [t]);

  // Memoize styles to prevent unnecessary re-renders during resize
  const containerStyle = useMemo(() => ({
    background: color,
    border: selected ? '2px dashed #3B82F6' : '2px dashed #9CA3AF',
    borderRadius: 12,
    padding: 16,
    width: '100%',
    height: '100%',
    boxSizing: 'border-box' as const,
    position: 'relative' as const,
  }), [color, selected]);

  const headerStyle = useMemo(() => ({
    position: 'absolute' as const,
    top: -12,
    left: 12,
    background: 'white',
    padding: '4px 12px',
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center' as const,
    gap: 8,
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    border: '1px solid #E5E7EB',
    zIndex: 1,
  }), []);

  return (
    <>
      <NodeResizer
        minWidth={300}
        minHeight={200}
        isVisible={selected}
        lineClassName="group-resize-line"
        handleClassName="group-resize-handle"
        color="#3B82F6"
      />
      <div style={containerStyle}>
        <div style={headerStyle}>
          {FABRIC_ICONS[data.type || data.resourceType] ? (
            <img
              src={FABRIC_ICONS[data.type || data.resourceType]}
              alt=""
              style={{ width: 16, height: 16, objectFit: 'contain', flexShrink: 0 }}
            />
          ) : (
            <FiPackage size={14} style={{ color: '#6B7280' }} />
          )}
          {editing ? (
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              onBlur={handleSave}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSave();
                if (e.key === 'Escape') {
                  setLabel(data.label);
                  setEditing(false);
                }
              }}
              style={{
                fontSize: 13,
                fontWeight: 600,
                border: '1px solid #D1D5DB',
                borderRadius: 4,
                padding: '2px 6px',
                width: 150,
              }}
              autoFocus
            />
          ) : (
            <span
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: '#374151',
                cursor: 'pointer',
              }}
              onClick={() => setEditing(true)}
            >
              {label || data.label}
            </span>
          )}
          {fabricFinOps?.kind === 'capacity' && (
            <span style={{
              fontSize: 10, fontWeight: 600,
              padding: '1px 6px', borderRadius: 10,
              background: fabricFinOps.sku === 'Trial' ? '#ECFDF5' : '#E0F7FA',
              color:      fabricFinOps.sku === 'Trial' ? '#065F46' : '#038387',
              border:     `1px solid ${fabricFinOps.sku === 'Trial' ? '#6EE7B7' : '#B2EBF2'}`,
              whiteSpace: 'nowrap', marginLeft: 2,
            }}>
              {fabricFinOps.sku === 'Trial'
                ? 'Trial · Gratuit'
                : `${fabricFinOps.sku} · ~€${fabricFinOps.cost.toLocaleString('fr-FR')}/mois`}
            </span>
          )}
          {fabricFinOps?.kind === 'workspace' && (
            <span style={{
              fontSize: 10, fontWeight: 500,
              padding: '1px 6px', borderRadius: 10,
              background: '#E8F5E9', color: '#2E7D32',
              border: '1px solid #C8E6C9',
              whiteSpace: 'nowrap', marginLeft: 2,
            }}>
              ~€{fabricFinOps.share.toLocaleString('fr-FR')}/mois
            </span>
          )}
          <button
            onClick={() => setEditing(!editing)}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              display: 'flex',
              alignItems: 'center',
            }}
            title={t('groupNode.edit')}
          >
            <FiEdit2 size={12} style={{ color: '#6B7280' }} />
          </button>
          <button
            onClick={data.onDelete}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              display: 'flex',
              alignItems: 'center',
            }}
            title={t('groupNode.delete')}
          >
            <FiX size={14} style={{ color: '#6B7280' }} />
          </button>
        </div>

        {editing && (
          <div
            style={{
              position: 'absolute',
              top: 30,
              left: 12,
              background: 'white',
              padding: 8,
              borderRadius: 6,
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              border: '1px solid #E5E7EB',
              display: 'flex',
              gap: 4,
              zIndex: 10,
            }}
          >
            {colors.map((c) => (
              <button
                key={c.value}
                onClick={() => {
                  setColor(c.value);
                  data.onUpdate?.(label, c.value);
                }}
                style={{
                  width: 24,
                  height: 24,
                  background: c.value,
                  border: color === c.value ? '2px solid #3B82F6' : '1px solid #D1D5DB',
                  borderRadius: 4,
                  cursor: 'pointer',
                }}
                title={c.name}
              />
            ))}
          </div>
        )}

        <div
          style={{
            marginTop: 20,
            fontSize: 12,
            color: '#9CA3AF',
            textAlign: 'center',
            pointerEvents: 'none',
          }}
        >
          {t('groupNode.dropHint')}
        </div>
      </div>
    </>
  );
});

GroupNode.displayName = 'GroupNode';

export default GroupNode;
